import type { CardButton, CardPayload, MediaType } from "./types";

export const MAX_BUTTONS = 8;
export const EMOJI_SHORTCUTS = ["🔥", "🎁", "🌐", "📢", "💬", "✅", "⭐", "👑", "🚀"];

const allowedByMedia: Record<MediaType, string[]> = {
  photo: ["image/jpeg", "image/png", "image/webp"],
  animation: ["image/gif"],
  video: ["video/mp4"]
};

export function normalizeUrl(value: string) {
  const trimmed = value.trim();
  if (/^t\.me\//i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

export function getButtonTextError(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.length > 64) return "Button text must be 64 characters or fewer";
  return null;
}

export function getButtonUrlError(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes(",")) return "Use only one URL per button";
  if (!/^https?:\/\//i.test(trimmed) && !/^t\.me\//i.test(trimmed)) {
    return "URL must start with http://, https://, or t.me/";
  }
  try {
    const parsed = new URL(normalizeUrl(trimmed));
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "URL must start with http://, https://, or t.me/";
    }
    return null;
  } catch {
    return "URL must start with http://, https://, or t.me/";
  }
}

export function isAllowedUrl(value: string) {
  return getButtonUrlError(value) === null;
}

export function validateMedia(file: File | null, mediaType: MediaType) {
  if (!file) return "กรุณาเลือกไฟล์ media ก่อนส่ง";
  const allowed = allowedByMedia[mediaType];
  if (!allowed.includes(file.type)) {
    return mediaType === "photo"
      ? "รองรับเฉพาะ JPG, PNG หรือ WEBP สำหรับรูปภาพ"
      : mediaType === "animation"
        ? "รองรับเฉพาะ GIF สำหรับ animation"
        : "รองรับเฉพาะ MP4 สำหรับ video";
  }
  if (file.size > 50 * 1024 * 1024) return "ไฟล์ต้องไม่เกิน 50MB";
  return null;
}

export function validateButtons(buttons: CardButton[]) {
  const active = buttons.filter((button) => button.text.trim() || button.url.trim());
  if (active.length > MAX_BUTTONS) return `ปุ่มต้องไม่เกิน ${MAX_BUTTONS} ปุ่ม`;
  for (const button of active) {
    if (!button.text.trim()) return "Button text is required";
    const textError = getButtonTextError(button.text);
    if (textError) return textError;
    if (!button.url.trim()) return "Button URL is required";
    const urlError = getButtonUrlError(button.url);
    if (urlError) return urlError;
  }
  return null;
}

export function validateCardPayload(payload: CardPayload, file: File | null) {
  if (!(payload.targetId ?? payload.channelId)) return "กรุณาเลือก channel หรือ group";
  if (!payload.caption.trim()) return "กรุณาเขียน caption";
  if (payload.caption.length > 1024) return "caption สำหรับ media ต้องไม่เกิน 1024 ตัวอักษร";
  const mediaError = validateMedia(file, payload.mediaType);
  if (mediaError) return mediaError;
  return validateButtons(payload.buttons);
}
